// 3rd party libs
import React, { useEffect, useState } from 'react';
import { BreadCrumbs } from 'components/StyledUi/FleetBreadCrumbs';
// import { MachineViewWatchdogStatusIcon } from './MachineViewWatchdogStatusIcon';
import { useRouter } from 'common/hooks/useRouter';
import { hasConfig, hasWatchdog } from '../../settings/buDefaultSettings';
import { fleetPageRoute } from '../../settings/fleetPageRoutes';
import { useMachineInfo } from '../../providers';
import { useMachineViewBreadCrumbs } from './useMachineViewBreadCrumbs';
import { MacineStatus } from './MachineStatus/MachineStatus';
import { TooltipWrapper } from 'components';
import Setting from 'icons/indexed-icons/Setting';
import { ConfigActiveTab } from 'types/protein';
import { JBTRoutes } from 'constants/routes';
import { useHistory } from 'react-router-dom';
import { useFleetRouter } from '../../hooks/useFleetRouter';

const baseClass = 'bread-crumbs';

const EditButton = ({
  handle,
  isLoading = false
}: {
  handle: () => void;
  isLoading?: boolean;
}): JSX.Element => (
  <TooltipWrapper Tooltip="Configure Machine" bottom>
    <button
      disabled={isLoading}
      type="button"
      className={`${baseClass}__edit ${isLoading ? `${baseClass}__edit--is-loading` : ''}`}
      onClick={handle}
    >
      <Setting />
    </button>
  </TooltipWrapper>
);

export const MachineViewBreadCrumbs = (): JSX.Element => {
  const { companyName, siteName, lineName, plantId, ...apiRest } = useMachineViewBreadCrumbs();
  const { description, businessUnit, lineId, isLoading } = useMachineInfo();

  const { machineId } = useMachineInfo() as { machineId: string };
  const { page } = useRouter(fleetPageRoute);

  if (!businessUnit && !isLoading) return <div>invalid business unit</div>;

  let items: Record<string, unknown>[] = [
    {
      label: companyName as string,
      isLoading,
      slug: undefined
    }
  ];

  if (page === 'machine' || page === 'line' || page === 'site') {
    items = [
      ...items,
      {
        label: siteName as string,
        slug: !plantId ? undefined : `/fleet/site/v2/${plantId}`,
        isLoading
      }
    ];

    if (page === 'line' || page === 'machine') {
      items = [
        ...items,
        {
          label: lineName as string,
          slug: !lineId ? undefined : `/fleet/line/v2/${lineId}`,
          isLoading
        }
      ];
    }

    if (page === 'machine') {
      items = [
        ...items,
        {
          label: description as string,
          isLoading,
          slug: !machineId ? undefined : `/fleet/machine/v2/${machineId}/machine-health/overview`
        }
      ];
    }
  }

  const showConfigButton = hasConfig?.[businessUnit as string];
  const showWatchdogIcon = hasWatchdog?.[businessUnit as string];

  const history = useHistory();
  const configRouteMap = {
    [ConfigActiveTab.MachineHealth]: JBTRoutes.machineHealthConfig,
    [ConfigActiveTab.MachinePerformance]: JBTRoutes.machinePerformanceConfig,
    [ConfigActiveTab.Alerts]: JBTRoutes.machineAlertsV2
  };

  const [{ view }] = useFleetRouter();
  const [activeTab, setActiveTab] = useState<ConfigActiveTab>(ConfigActiveTab.MachineHealth);
  const viewToTabMap: Partial<Record<string, ConfigActiveTab>> = {
    'machine-health': ConfigActiveTab.MachineHealth,
    'machine-performance': ConfigActiveTab.MachinePerformance,
    'alerts': ConfigActiveTab.Alerts
  };

  useEffect(() => {
    if (view && viewToTabMap[view]) {
      setActiveTab(viewToTabMap[view]!);
    } else {
      setActiveTab(ConfigActiveTab.MachineHealth);
    }
  }, [view]);

  const handleEdit = () => {
    history.push(configRouteMap[activeTab].replace(':machineId', machineId));
  };

  return (
    <BreadCrumbs {...{ items, ...apiRest }}>
      {showWatchdogIcon && <MacineStatus />}
      {(businessUnit === 'pemea' || businessUnit === 'pna') && (
        <EditButton handle={handleEdit} isLoading={isLoading} />
      )}
    </BreadCrumbs>
  );
};
